import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import useDebounce from "../../hooks/useDebounce";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import { IconButton, Typography, FormControl, InputLabel, Select as MuiSelect, MenuItem, TextField } from "@mui/material";
import NotFound from "../../components/not-found/NotFound";
import Pagination from "react-js-pagination";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CloneIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toastMessage } from "../../utils/toastMessage";
import Loader from "../../comman/Loader";
import { segment } from "../../routes/route";
import common from "../../routes/common";
import findSerialNumber from "../../utils/findSerialNumber";
import { Select, DatePicker } from "../../components/Form";
import { getLocalStorage, setLocalStorage } from "../../config/config";
import {
  getSegmentsListApi,
  deleteSegmentApi,
  cloneSegmentApi,
  backfillSegmentApi,
} from "../../services/segmentManagement";
import { SecondaryButton } from "../../components/Button/Button";
import { dateFormatUtc } from "../../utils/dateFormat";

const { NOT_FOUND } = common;
const { SEGMENT_BUILDER, SEGMENT_DETAIL } = segment;

const SegmentList = () => {
  const navigate = useNavigate();
  const [searchParam, setSearchParam] = useSearchParams();
  let activePage = +searchParam.get("page") || 1;
  let pageLimit = +searchParam.get("limit") || 20;
  const [segments, setSegments] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(
    getLocalStorage("segmentList", "search") || ""
  );
  const debounceValue = useDebounce(search || "", 300);
  const [statusFilter, setStatusFilter] = useState(
    searchParam.get("status") || ""
  );
  const [syncStatusFilter, setSyncStatusFilter] = useState(
    searchParam.get("syncStatus") || ""
  );
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [timeRange, setTimeRange] = useState(searchParam.get("timeRange") || "");
  const [timeRangeStartDate, setTimeRangeStartDate] = useState(searchParam.get("timeRangeStartDate") || "");
  const [timeRangeEndDate, setTimeRangeEndDate] = useState(searchParam.get("timeRangeEndDate") || "");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [backfillDialog, setBackfillDialog] = useState({ open: false, id: null });

  // Time range options
  const timeRangeOptions = [
    { value: "today", label: "Today" },
    { value: "yesterday", label: "Yesterday" },
    { value: "last7days", label: "Last 7 Days" },
    { value: "last30days", label: "Last 30 Days" },
    { value: "thisMonth", label: "This Month" },
    { value: "lastMonth", label: "Last Month" },
    { value: "custom", label: "Custom Range" }
  ];

  useEffect(() => {
    setLocalStorage("segmentList", "search", search);
  }, [search]);

  useEffect(() => {
    if (Number(activePage) === 1) {
      searchParam.delete("page");
    }
    setSearchParam(searchParam);
    fetchSegments();
  }, [searchParam, debounceValue]);

  const fetchSegments = async () => {
    try {
      showLoader();
      const params = {
        page: activePage,
        limit: pageLimit,
        ...(debounceValue && { search: debounceValue }),
        ...(statusFilter && { status: statusFilter }),
        ...(syncStatusFilter && { syncStatus: syncStatusFilter }),
        ...(startDate && { startDate: dateFormatUtc(startDate) }),
        ...(endDate && { endDate: dateFormatUtc(endDate) }),
        ...(timeRange && { timeRange }),
        ...(timeRangeStartDate && { timeRangeStartDate }),
        ...(timeRangeEndDate && { timeRangeEndDate }),
      };
      const data = await getSegmentsListApi(params);
      if (data.success) {
        setSegments(data.data || []);
        setTotal(data.pagination?.total || 0);
      } else {
        setSegments([]);
        setTotal(0);
        toastMessage(data.message || "Error fetching segments", "error");
      }
    } catch (error) {
      console.log("error", error);
      setSegments([]);
      setTotal(0);
      toastMessage(error.response?.data?.message || "Error fetching segments", "error");
    } finally {
      hideLoader();
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSegmentApi(deleteDialog.id);
      toastMessage("Segment deleted successfully", "success");
      setDeleteDialog({ open: false, id: null });
      fetchSegments();
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error deleting segment", "error");
    }
  };

  const handleClone = async (segmentId) => {
    try {
      const segment = segments.find((s) => s._id === segmentId);
      await cloneSegmentApi(segmentId, {
        name: `${segment.name} (Copy)`,
        interakt_tag: `${segment.interakt_tag}_copy_${Date.now()}`,
      });
      toastMessage("Segment cloned successfully", "success");
      fetchSegments();
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error cloning segment", "error");
    }
  };

  const handleBackfill = async () => {
    try {
      const data = await backfillSegmentApi(backfillDialog.id);
      if (data.success) {
        toastMessage(`Backfill queued for ${data.queued || 0} users`, "success");
        setBackfillDialog({ open: false, id: null });
        fetchSegments();
      } else {
        toastMessage(data.message || "Error starting backfill", "error");
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error starting backfill", "error");
    }
  };

  const statusHandler = (value) => {
    if (statusFilter === value) {
      searchParam.delete("status");
      setStatusFilter("");
    } else {
      searchParam.set("status", value);
      setStatusFilter(value);
    }
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  const syncStatusHandler = (value) => {
    if (syncStatusFilter === value) {
      searchParam.delete("syncStatus");
      setSyncStatusFilter("");
    } else {
      searchParam.set("syncStatus", value);
      setSyncStatusFilter(value);
    }
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  const handlePageChange = (event) => {
    searchParam.set("page", event);
    setSearchParam(searchParam);
  };

  const handlePageLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    searchParam.set("limit", newLimit);
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  // Time range handler
  const handleTimeRangeChange = (value) => {
    setTimeRange(value);
    if (value !== 'custom') {
      setTimeRangeStartDate("");
      setTimeRangeEndDate("");
      searchParam.delete("timeRangeStartDate");
      searchParam.delete("timeRangeEndDate");
    }
    if (value) {
      searchParam.set("timeRange", value);
    } else {
      searchParam.delete("timeRange");
    }
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  // Time range date change handler
  const handleTimeRangeDateChange = (type, value) => {
    if (type === 'start') {
      setTimeRangeStartDate(value);
      if (value) {
        searchParam.set("timeRangeStartDate", value);
      } else {
        searchParam.delete("timeRangeStartDate");
      }
    } else {
      setTimeRangeEndDate(value);
      if (value) {
        searchParam.set("timeRangeEndDate", value);
      } else {
        searchParam.delete("timeRangeEndDate");
      }
    }
    searchParam.delete("page");
    setSearchParam(searchParam);
  };

  // Apply filter function
  const applyFilter = () => {
    searchParam.delete("page");
    setSearchParam(searchParam);
    fetchSegments();
  };

  const refreshHandler = () => {
    setSearch("");
    setStatusFilter("");
    setSyncStatusFilter("");
    setStartDate(null);
    setEndDate(null);
    setTimeRange("");
    setTimeRangeStartDate("");
    setTimeRangeEndDate("");
    searchParam.delete("limit");
    searchParam.delete("page");
    searchParam.delete("status");
    searchParam.delete("syncStatus");
    searchParam.delete("startDate");
    searchParam.delete("endDate");
    searchParam.delete("timeRange");
    searchParam.delete("timeRangeStartDate");
    searchParam.delete("timeRangeEndDate");
    setSearchParam(searchParam);
    fetchSegments();
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      draft: {
        backgroundColor: "#6c757d",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      ready: {
        backgroundColor: "#17a2b8",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      applied: {
        backgroundColor: "#28a745",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      paused: {
        backgroundColor: "#ffc107",
        color: "#212529",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
    };
    return statusStyles[status] || statusStyles.draft;
  };

  const getSyncStatusBadge = (status) => {
    const statusStyles = {
      synced: {
        backgroundColor: "#28a745",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      pending: {
        backgroundColor: "#ffc107",
        color: "#212529",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      failed: {
        backgroundColor: "#dc3545",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
      not_synced: {
        backgroundColor: "#6c757d",
        color: "#fff",
        padding: "4px 8px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: "500",
        display: "inline-block"
      },
    };
    return statusStyles[status] || statusStyles.not_synced;
  };

  return (
    <>
      {loader}
      <div className="WrapperArea">
        <div className="WrapperBox">
          <div className="TitleBox">
            <h4 className="Title">Segment Management</h4>
            <div className="block_user_button">
              <button onClick={() => navigate(SEGMENT_BUILDER)}>
                <AddIcon />
                Create Segment
              </button>
            </div>
          </div>

          <div className="banner_wrapper">
            <div className="FilterBox FilterArea" style={{ flexDirection: 'column' }}>
              {/* Row 1: Search Bar and Basic Filters */}
              <div className="group_filter_value" style={{ 
                marginBottom: '15px', 
                display: 'flex !important', 
                flexWrap: 'wrap !important', 
                gap: '15px', 
                alignItems: 'center',
                width: '100%',
                flexDirection: 'row'
              }}>
                {/* Search Bar */}
                <div className="form-group search_md_size userSearch" >
                  <input
                    type="search"
                    className={`form-control ${search && "active"}`}
                    placeholder="Search segments..."
                    value={search || ""}
                    style={{ "--color": "#FF8989" }}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="form-group select_sm_width" >
                  <Select
                    className={`form-control ${statusFilter && "active"}`}
                    value={statusFilter || ""}
                    onChange={(e) => statusHandler(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="draft">Draft</option>
                    <option value="ready">Ready</option>
                    <option value="applied">Applied</option>
                    <option value="paused">Paused</option>
                  </Select>
                </div>

                {/* Sync Status Filter */}
                <div className="form-group select_sm_width" >
                  <Select
                    className={`form-control ${syncStatusFilter && "active"}`}
                    value={syncStatusFilter || ""}
                    onChange={(e) => syncStatusHandler(e.target.value)}
                  >
                    <option value="">All Sync Status</option>
                    <option value="synced">Synced</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                    <option value="not_synced">Not Synced</option>
                  </Select>
                </div>
                <SecondaryButton onClick={applyFilter}>
                    Apply
                  </SecondaryButton>
                  <SecondaryButton onClick={refreshHandler}>
                    Clear
                  </SecondaryButton>
              </div>

              {/* Row 2: Date Filters and Time Range */}
              {/* <div className="group_filter_value" style={{ 
                display: 'flex !important', 
                flexWrap: 'wrap !important', 
                gap: '15px', 
                alignItems: 'center',
                width: '100%',
                flexDirection: 'row'
              }}>
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <DatePicker
                    label="Start Date"
                    selected={startDate}
                    onChange={(date) => setStartDate(date)}
                    placeholderText="Start Date"
                    className="form-control"
                  />
                </div>

                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <DatePicker
                    label="End Date"
                    selected={endDate}
                    onChange={(date) => setEndDate(date)}
                    placeholderText="End Date"
                    className="form-control"
                  />
                </div>

                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <InputLabel>Time Range</InputLabel>
                    <MuiSelect
                      value={timeRange}
                      onChange={(e) => handleTimeRangeChange(e.target.value)}
                      label="Time Range"
                    >
                      <MenuItem value="">
                        <em>All Time Ranges</em>
                      </MenuItem>
                      {timeRangeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </MuiSelect>
                  </FormControl>
                </div>
                
                {timeRange === 'custom' && (
                  <>
                    <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                      <TextField
                        size="small"
                        type="date"
                        variant="outlined"
                        label="Start Date"
                        value={timeRangeStartDate}
                        onChange={(e) => handleTimeRangeDateChange('start', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                    <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                      <TextField
                        size="small"
                        type="date"
                        variant="outlined"
                        label="End Date"
                        value={timeRangeEndDate}
                        onChange={(e) => handleTimeRangeDateChange('end', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                  </>
                )}

                <div className="form-group refresh_button" style={{ minWidth: '120px', flex: '0 0 auto', display: 'flex', gap: '10px' }}>
                  <SecondaryButton onClick={applyFilter}>
                    Apply
                  </SecondaryButton>
                  <SecondaryButton onClick={refreshHandler}>
                    Clear
                  </SecondaryButton>
                </div>
              </div> */}
            </div>
          </div>

          <div className="content-table">
            <table>
              <thead>
                <tr>
                  <th>S. No.</th>
                  <th>Name</th>
                  <th>Interakt Tag</th>
                  <th>Status</th>
                  {/* <th>Users</th> */}
                  <th>Created By</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {segments.length > 0 ? (
                  segments.map((segment, index) => (
                    <tr key={segment._id}>
                      <td>{findSerialNumber(index, activePage, pageLimit)}</td>
                      <td>
                        <div>
                          <strong>{segment.name}</strong>
                          {segment.description && (
                            <div style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                              {segment.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          style={{
                            backgroundColor: "#17a2b8",
                            color: "#fff",
                            padding: "4px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            display: "inline-block"
                          }}
                        >
                          {segment.interakt_tag}
                        </span>
                      </td>
                      <td>
                        <span style={getStatusBadge(segment.status)}>
                          {segment.status}
                        </span>
                      </td>
                      {/* <td>{segment.matching_users_count || 0}</td> */}
                     
                      <td>{segment.created_by?.name || "Unknown"}</td>
                      <td>
                        <div className="action_button">
                          <IconButton
                            aria-label="view"
                            className="px-2 py-0"
                            onClick={() => navigate(`${SEGMENT_DETAIL}/${segment._id}`)}
                            title="View Details"
                          >
                            <ViewIcon className="edit_icon" />
                          </IconButton>
                          <IconButton
                            aria-label="edit"
                            className="px-2 py-0"
                            onClick={() => navigate(`${SEGMENT_BUILDER}/${segment._id}`)}
                            title="Edit"
                          >
                            <EditIcon className="edit_icon" />
                          </IconButton>
                          {/* <IconButton
                            aria-label="clone"
                            className="px-2 py-0"
                            onClick={() => handleClone(segment._id)}
                            title="Clone"
                          >
                            <CloneIcon className="edit_icon" />
                          </IconButton> */}
                          {/* <IconButton
                            aria-label="backfill"
                            className="px-2 py-0"
                            onClick={() => setBackfillDialog({ open: true, id: segment._id })}
                            title="Backfill"
                          >
                            <PlayIcon className="edit_icon" />
                          </IconButton> */}
                          <IconButton
                            aria-label="delete"
                            className="px-2 py-0"
                            onClick={() => setDeleteDialog({ open: true, id: segment._id })}
                            title="Delete"
                          >
                            <DeleteIcon className="delete_icon" />
                          </IconButton>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <NotFound
                    cols={9}
                    msg="No segments found!"
                    icon={NOT_FOUND}
                    loader={loader}
                  />
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination-wrapper mt-4" style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "15px",
            marginTop: "20px"
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px"
            }}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Show:
              </Typography>
              <select
                value={pageLimit}
                onChange={(e) => handlePageLimitChange(e)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                  minWidth: "80px",
                  cursor: "pointer"
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <Typography variant="body2" style={{ color: "#666" }}>
                per page
              </Typography>
            </div>

            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}>
              <Typography variant="body2" style={{ color: "#666" }}>
                Showing {((activePage - 1) * pageLimit) + 1} to {Math.min(activePage * pageLimit, total)} of {total} items
              </Typography>
            </div>

            {total > pageLimit && (
              <div className="Pagination">
                <Pagination
                  activePage={activePage}
                  itemsCountPerPage={pageLimit}
                  totalItemsCount={total}
                  pageRangeDisplayed={5}
                  onChange={handlePageChange}
                  itemClass="page-item"
                  linkClass="page-link"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {deleteDialog.open && (
        <div className="modal show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Segment</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setDeleteDialog({ open: false, id: null })}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this segment? This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteDialog({ open: false, id: null })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Backfill Modal */}
      {backfillDialog.open && (
        <div className="modal show" style={{ display: "block" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Backfill Segment</h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => setBackfillDialog({ open: false, id: null })}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  This will evaluate all users against this segment. This may take some time. Continue?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setBackfillDialog({ open: false, id: null })}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleBackfill}
                >
                  Start Backfill
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SegmentList;
